import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { authAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { MapPin, Navigation, Loader2, Store, User, Mail, Lock, Phone, Home } from 'lucide-react';

const MEXICO_STATES = [
  "Aguascalientes","Baja California","Baja California Sur","Campeche","Chiapas",
  "Chihuahua","Ciudad de México","Coahuila","Colima","Durango","Estado de México",
  "Guanajuato","Guerrero","Hidalgo","Jalisco","Michoacán","Morelos","Nayarit",
  "Nuevo León","Oaxaca","Puebla","Querétaro","Quintana Roo","San Luis Potosí",
  "Sinaloa","Sonora","Tabasco","Tamaulipas","Tlaxcala","Veracruz","Yucatán","Zacatecas"
];

const Register = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [createStore, setCreateStore] = useState(false);

  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    street: '', neighborhood: '', zip_code: '', city: '', state: '',
    lat: null, lng: null, store_name: '',
  });

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: 'Geolocalizacion no soportada', variant: 'destructive' });
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set('lat', pos.coords.latitude);
        set('lng', pos.coords.longitude);
        setGeoLoading(false);
        toast({ title: 'Ubicacion capturada' });
      },
      () => {
        setGeoLoading(false);
        toast({ title: 'No se pudo obtener ubicacion', variant: 'destructive' });
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast({ title: 'Completa nombre, email y contraseña', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: 'buyer',
        zip_code: form.zip_code,
        state: form.state,
        lat: form.lat,
        lng: form.lng,
        create_store: createStore,
        store_name: form.store_name || form.name,
      };
      if (form.street) {
        payload.delivery_address = {
          recipient: form.name,
          phone: form.phone,
          street: form.street,
          neighborhood: form.neighborhood,
          zip_code: form.zip_code,
          city: form.city,
          state: form.state,
        };
      }
      const response = await authAPI.register(payload);
      login(response.user);
      toast({ title: '¡Cuenta creada!', description: 'Bienvenido a Zona Libre' });
      navigate('/');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Error al registrar', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <img src="/logo-zonalibre.png" alt="Zona Libre" className="h-12 mx-auto mb-3" />
          <h1 className="text-2xl font-bold">Crea tu cuenta</h1>
          <p className="text-gray-500 text-sm">Completa tus datos para empezar a comprar y vender</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Account info */}
            <Card className="p-6 space-y-4" data-testid="register-account-card">
              <h2 className="font-bold text-lg flex items-center gap-2"><User className="w-5 h-5 text-orange-500" /> Datos de cuenta</h2>
              <div>
                <Label>Nombre completo *</Label>
                <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Juan Perez" required data-testid="register-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="tu@email.com" required data-testid="register-email" />
              </div>
              <div>
                <Label>Contraseña *</Label>
                <Input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min. 6 caracteres" required data-testid="register-password" />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+52 55 1234 5678" data-testid="register-phone" />
              </div>

              {/* Store toggle */}
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Store className="w-5 h-5 text-orange-500" />
                    <span className="font-semibold text-sm">Abrir mi tienda gratis</span>
                  </div>
                  <Switch checked={createStore} onCheckedChange={setCreateStore} data-testid="create-store-toggle" />
                </div>
                {createStore && (
                  <div className="mt-3 space-y-3 bg-orange-50 p-3 rounded-lg">
                    <div>
                      <Label>Nombre de tu tienda</Label>
                      <Input value={form.store_name} onChange={e => set('store_name', e.target.value)} placeholder="Mi Tienda" data-testid="register-store-name" />
                    </div>
                    <Button type="button" variant="outline" onClick={getLocation} disabled={geoLoading} className="w-full" data-testid="register-gps-btn">
                      {geoLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Navigation className="w-4 h-4 mr-2" />}
                      {form.lat ? 'Ubicacion fijada' : 'Fijar ubicacion GPS'}
                    </Button>
                    {form.lat && (
                      <p className="text-xs text-green-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> {form.lat.toFixed(4)}, {form.lng.toFixed(4)}</p>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Right: Address */}
            <Card className="p-6 space-y-4" data-testid="register-address-card">
              <h2 className="font-bold text-lg flex items-center gap-2"><Home className="w-5 h-5 text-orange-500" /> Direccion de envio (opcional)</h2>
              <p className="text-xs text-gray-400">Completa para agilizar tu primera compra</p>
              <div>
                <Label>Calle y numero</Label>
                <Input value={form.street} onChange={e => set('street', e.target.value)} placeholder="Av. Reforma 123" data-testid="register-street" />
              </div>
              <div>
                <Label>Colonia</Label>
                <Input value={form.neighborhood} onChange={e => set('neighborhood', e.target.value)} placeholder="Col. Centro" data-testid="register-neighborhood" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Codigo Postal</Label>
                  <Input value={form.zip_code} onChange={e => set('zip_code', e.target.value.replace(/\D/g,'').slice(0,5))} placeholder="06600" maxLength={5} data-testid="register-zip" />
                </div>
                <div>
                  <Label>Ciudad</Label>
                  <Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Ciudad de Mexico" data-testid="register-city" />
                </div>
              </div>
              <div>
                <Label>Estado</Label>
                <select value={form.state} onChange={e => set('state', e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="register-state">
                  <option value="">Selecciona</option>
                  {MEXICO_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </Card>
          </div>

          <div className="mt-6 max-w-md mx-auto space-y-4">
            <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-lg" data-testid="register-submit-btn">
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creando...</> : 'Crear mi cuenta'}
            </Button>
            <p className="text-center text-sm text-gray-500">
              Ya tienes cuenta? <Link to="/login" className="text-orange-500 hover:underline font-medium">Ingresa aqui</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
